import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffect, useState, useRef } from "react";
import { ChevronRight, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Category } from "@/data/categories";
import { categoryImages } from "@/data/categoryImages";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useTranslation } from "react-i18next";

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MegaMenu = ({ isOpen, onClose }: MegaMenuProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [fadeKey, setFadeKey] = useState(0);
  const handleScrollToTop = useScrollToTop();
  const { t } = useTranslation('categories');

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name");

      if (error) {
        console.error("Error fetching categories:", error);
        return;
      }

      if (data) {
        setCategories(data.map((cat: any) => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          subcategories: Array.isArray(cat.subcategories) ? cat.subcategories : []
        })));
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (isOpen) setActiveIndex(0);
  }, [isOpen]);

  const handleCategoryHover = (idx: number) => {
    if (idx !== activeIndex) {
      setActiveIndex(idx);
      setFadeKey(prev => prev + 1);
    }
  };

  const activeCategory = categories[activeIndex];
  const activeCategoryImage = activeCategory ? categoryImages[activeCategory.id] : undefined;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={onClose}
        />
      )}

      {/* Menu */}
      <div className={cn(
        "fixed left-0 top-16 w-full bg-card border border-border shadow-2xl z-40 transform transition-all duration-300",
        isOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
      )}>
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            {/* Left: Category titles */}
            <aside className="md:col-span-4 lg:col-span-3 border-r border-border pr-4">
              <ul className="space-y-0.5">
                {categories.map((category, idx) => {
                  const active = idx === activeIndex;
                  const thumbImage = categoryImages[category.id];
                  return (
                    <li key={category.id}>
                      <button
                        onClick={() => {
                          handleScrollToTop(`/category/${category.slug}`);
                          onClose();
                        }}
                        onMouseEnter={() => handleCategoryHover(idx)}
                        className={cn(
                          "flex items-center gap-3 justify-between rounded-md px-3 py-2.5 hover:text-primary hover:bg-muted/60 transition-all duration-200 w-full text-left",
                          active
                            ? "bg-muted text-primary border-l-2 border-primary"
                            : "text-foreground border-l-2 border-transparent"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {thumbImage && (
                            <img
                              src={thumbImage}
                              alt=""
                              className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                            />
                          )}
                          <span className="font-display font-semibold text-sm">
                            {t(`categoryNames.${category.slug}`, category.name)}
                          </span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: Subcategories of hovered category */}
            <section className="md:col-span-8 lg:col-span-9 pl-6">
              <div
                key={fadeKey}
                className="rounded-2xl border border-border bg-card p-6 h-full animate-fade-in"
              >
                {/* Header with image */}
                <div className="flex items-start gap-5 mb-5">
                  {activeCategoryImage && (
                    <img
                      src={activeCategoryImage}
                      alt={activeCategory?.name || ""}
                      className="w-24 h-24 rounded-xl object-cover flex-shrink-0 shadow-sm"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-display font-bold text-xl text-foreground">
                      {t(`categoryNames.${activeCategory?.slug}`, activeCategory?.name)}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {t(`categoryDescriptions.${activeCategory?.slug}`, '')}
                    </p>
                  </div>
                </div>

                {/* Subcategories grid */}
                <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {activeCategory?.subcategories.map((sub) => {
                    const categorySlug = activeCategory?.slug;
                    const subcategorySlug = sub
                      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase()
                      .replace(/'/g, '')
                      .replace(/&/g, 'and')
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    return (
                      <li key={sub} className="story-link">
                        <Button
                          variant="ghost"
                          className="h-auto p-1.5 font-normal text-muted-foreground hover:text-primary justify-start w-full text-left text-sm"
                          onClick={() => {
                            handleScrollToTop(`/category/${categorySlug}?subcategory=${subcategorySlug}`);
                            onClose();
                          }}
                        >
                          {t(`subcategoryNames.${subcategorySlug}`, sub)}
                        </Button>
                      </li>
                    );
                  })}
                </ul>

                {/* Shop All link */}
                {activeCategory && (
                  <div className="mt-5 pt-4 border-t border-border">
                    <Button
                      variant="link"
                      className="p-0 h-auto font-display font-semibold text-primary hover:text-primary/80 gap-1.5"
                      onClick={() => {
                        handleScrollToTop(`/category/${activeCategory.slug}`);
                        onClose();
                      }}
                    >
                      {t('shopAllProducts', 'Shop All Products')}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};
